import { Platform } from '@angular/cdk/platform';

import { Component, DestroyRef, inject, ViewContainerRef, DOCUMENT } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FuseConfigService } from '@fuse/services/config.service';
import { environment } from 'environments/environment';
import { I18nService } from './services/i18n.service';
import { SentryService } from './services/sentry.service';
import { Router, RouterOutlet } from '@angular/router';
import { FuseSplashScreenService } from '@fuse/services/splash-screen.service';
import { _ } from '@ngx-translate/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { ToastrService } from 'ngx-toastr';
import { filter, take } from 'rxjs';
import { CurrentUserService } from './core/auth/services/current-user.service';
import { NavPermissionService } from './navigation/services/nav-permission.service';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { ThemeService } from './modules/layout/components/toolbar/theme.service';

@Component({
  selector: 'app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet],
  // NOT OnPush: this is the root of the whole routed tree, and most feature pages beneath it
  // are still Default-strategy with plain mutable fields (pre-signals migration, see CLAUDE.md).
  // OnPush here silently blocks zone-driven CD from ever reaching those pages after any async
  // update that isn't a signal write or a DOM event bound within this subtree.
})
export class AppComponent {
  // Read reflectively off the root component instance by ngx-color-picker's ColorPickerDirective
  // when `cpUseRootViewContainer` is set (see colorpicker.component.html, color-menu.component.html)
  // — must stay public and keep this exact name.
  public readonly vcRef = inject(ViewContainerRef);

  private readonly _document = inject<Document>(DOCUMENT);
  private readonly _fuseConfigService = inject(FuseConfigService);
  // Unused directly, but injecting it triggers its constructor side effect (hides the splash
  // screen on first navigation) — this is the only place in the app that injects it.
  private readonly _fuseSplashScreenService = inject(FuseSplashScreenService);
  private readonly _fuseNavigationService = inject(FuseNavigationService);
  private readonly _platform = inject(Platform);
  private readonly _i18nService = inject(I18nService);
  private readonly _sentryService = inject(SentryService);
  private readonly _themeService = inject(ThemeService);
  private readonly _router = inject(Router);
  private readonly _ngxPermissionService = inject(NgxPermissionsService);
  private readonly _currentUserService = inject(CurrentUserService);
  private readonly _navPermissionService = inject(NavPermissionService);
  private readonly _toastrService = inject(ToastrService);
  private readonly _destroyRef = inject(DestroyRef);

  constructor() {
    this._fuseNavigationService.register('main', []);
    this._fuseNavigationService.setCurrentNavigation('main');

    // INIT TRASNLATIONS BEHAVIOR
    this._i18nService.init();

    // Add is-mobile class to the body if the platform is mobile
    if (this._platform.ANDROID || this._platform.IOS) {
      this._document.body.classList.add('is-mobile');
    }

    // INIT SENTRY MONITORING
    if (environment.sentry) {
      this._sentryService.init();
    }

    this._fuseConfigService.config.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((config: any) => {
      // Color theme - snapshot first: DOMTokenList is live, so removing while iterating it
      // directly (for-of/indexed) can skip entries.
      const classList = this._document.body.classList;
      Array.from(classList)
        .filter((className: string) => className.startsWith('theme-'))
        .forEach((className) => classList.remove(className));
      this._document.body.classList.add(config.colorTheme);
    });

    this._userChangedListener();
  }

  private _updatePermissions() {
    // RESET PERMISSIONS
    this._ngxPermissionService.flushPermissions();
    // APPLY NEW PERMISSIONS
    this._ngxPermissionService.loadPermissions(this._currentUserService.grants$());
  }

  private _validateNavigationAndCurrentRoute() {
    // wait for first navigation end to check if the use has permissions to access the current route
    this._navPermissionService.firstNavigationEnded
      .pipe(
        filter((v) => v === true),
        take(1),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(async (firstNavigationEnded) => {
        if (firstNavigationEnded) {
          if (!(await this._navPermissionService.isValidCurrentRoute())) {
            this._toastrService.error(
              this._i18nService.translate(_('Non hai i permessi per accedere a questa pagina')),
            );
            this._router.navigate(['dashboard']);
          }
        }
      });
  }

  private _userChangedListener() {
    this._updatePermissions();
    this._currentUserService.onRefresh.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this._updatePermissions();
      this._validateNavigationAndCurrentRoute();

      if (this._currentUserService.currentUser$()?.username === 'd.finardi') {
        window.console.log = window.console.info = () => {};
      }
    });
  }
}
